
import React from 'react';
import { cn } from '../../lib/utils';

interface DocumentProgressRingProps {
  status: 'not-started' | 'in-progress' | 'complete' | 'overdue';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const DocumentProgressRing: React.FC<DocumentProgressRingProps> = ({
  status,
  size = 'md',
  className
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  const strokeWidth = size === 'sm' ? 1.5 : 2;
  const radius = size === 'sm' ? 6 : size === 'md' ? 10 : 14;
  const circumference = 2 * Math.PI * radius;

  const getStatusStyles = () => {
    switch (status) {
      case 'complete':
        return {
          ring: 'stroke-emerald-500',
          fill: 'fill-emerald-50',
          icon: (
            <svg className="w-full h-full" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 12l2 2 4-4"
                stroke="currentColor"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-emerald-500"
              />
            </svg>
          )
        };
      case 'overdue':
        return {
          ring: 'stroke-red-500',
          fill: 'fill-red-50',
          icon: (
            <svg className="w-full h-full" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 9v4m0 4h.01"
                stroke="currentColor"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-red-500"
              />
            </svg>
          )
        };
      case 'in-progress':
        return {
          ring: 'stroke-amber-500',
          fill: 'fill-amber-50',
          icon: (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-1 h-1 bg-amber-500 rounded-full animate-pulse" />
            </div>
          )
        };
      default:
        return {
          ring: 'stroke-gray-300',
          fill: 'fill-gray-50',
          icon: null
        };
    }
  };

  const { ring, fill, icon } = getStatusStyles();

  return (
    <div className={cn('relative flex items-center justify-center', sizeClasses[size], className)}>
      <svg
        className="w-full h-full transform -rotate-90"
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle
          cx="12"
          cy="12"
          r={radius}
          className={cn(fill, ring)}
          strokeWidth={strokeWidth}
          fill="currentColor"
        />
        {status === 'in-progress' && (
          <circle
            cx="12"
            cy="12"
            r={radius}
            className="stroke-amber-500"
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * 0.75}
            strokeLinecap="round"
          />
        )}
      </svg>
      {icon && (
        <div className="absolute inset-0 flex items-center justify-center">
          {icon}
        </div>
      )}
    </div>
  );
};

export default DocumentProgressRing;
